import { Box, Button, Container, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { styled } from '@mui/material/styles';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import {
  useGetParkingTypeDataMutation,
  useSaveParkingTypeMutation,
  useUpdateParkingTypeMutation,
  useUploadImageMutation,
} from '../store/api/parkingTypeApi';
import Loading from './Loading';
import { useGetEnumDataMutation } from '../store/api/enumApi';
import { getSpecificEnumData, setEnumData } from '../store/state/enumSlice';

type Props = {};

const ParkingSchema = Yup.object().shape({
  typeName: Yup.string()
    .required('Service type name is required')
    .matches(/^[a-zA-Z\s.-]+$/, { message: 'Name must be alphabetic' })
    .trim(),
  discountOffer: Yup.number().when('discountType', {
    is: (discountType: any) => discountType !== 'DTND',
    then: Yup.number().required('This field is required'),
    otherwise: Yup.number().optional().nullable(),
  }),
  discountType: Yup.string().required('Discount type is required'),
  bannerType: Yup.number().required('Banner type is required'),
});

const ResponsiveBox = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

const FormikBox = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    marginLeft: '0px',
  },
  [theme.breakpoints.up('md')]: {
    marginLeft: '20px',
  },
}));

const AddOrUpdateParking = (props: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState({
    typeName: '',
    discountOffer: '',
    discountType: '',
    bannerType: 0,
    status: true,
  });
  const [isImgUrl, setIsImgUrl] = useState(false);
  const { state }: any = useLocation();
  const token = JSON.parse(localStorage.getItem('login') || '{}').token;
  const [parkingManagementData, setParkingManagementData] = useState([]);
  const [getParkingTypeData, getParkingTypeDataResponse] = useGetParkingTypeDataMutation();
  const [saveParkingType, saveParkingDataResponse] = useSaveParkingTypeMutation();
  const [updateParkingType, updateParkingDataResponse] = useUpdateParkingTypeMutation();
  const [uploadImage, uploadImageResponse] = useUploadImageMutation();
  const [discountType, setDiscountType] = useState([]);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [submitData, setSubmitData] = useState<any | null>();

  const getParkingTypeDataAsync = async (rowPerPages: any, pages: number, orders: string, typeNames = '') => {
    await getParkingTypeData({
      token: token,
      payload: { offset: rowPerPages, pageNumber: pages, sortOrder: orders, typeName: typeNames },
    });
  };

  useEffect(() => {
    if (token) {
      getParkingTypeDataAsync(rowsPerPage, page, order);
    }
  }, []);

  useEffect(() => {
    if (getParkingTypeDataResponse.isSuccess) {
      setParkingManagementData(getParkingTypeDataResponse.data.data);
    }
    if (getParkingTypeDataResponse.isError) {
      let errorData: any = getParkingTypeDataResponse.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error('Something went wrong ' + msg || '');
    }
  }, [getParkingTypeDataResponse.isSuccess, getParkingTypeDataResponse.isError]);

  // parking image ref
  const parkingImageInput: any = useRef();
  // parking image state
  const [parkingImage, setParkingImage] = useState<null | any>();
  const handleAddOrUpdateParking = (values: any, action: any) => {
    if (!state.editMode) {
      setSubmitData({
        typeName: values.typeName,
        discountOffer: values.discountOffer,
        discountType: values.discountType,
        bannerType: values.bannerType,
        status: values.status,
      });
      const formData = new FormData();
      formData.append('file', parkingImage);
      uploadImage({
        token: token,
        payload: formData,
      });
    } else {
      setSubmitData({
        id: state.editData.id,
        typeName: values.typeName,
        discountOffer: values.discountOffer,
        discountType: values.discountType,
        bannerType: values.bannerType,
        status: values.status,
      });
      if (!isImgUrl) {
        const formData = new FormData();
        formData.append('file', parkingImage);
        uploadImage({
          token: token,
          payload: formData,
        });
      } else {
        updateParkingType({
          token: token,
          payload: {
            id: state.editData.id,
            typeName: values.typeName,
            discountOffer: values.discountOffer,
            discountType: values.discountType,
            bannerType: values.bannerType,
            status: values.status,
            imagePath: parkingImage.split('?')[0].split('com/')[1],
          },
        });
      }
    }
  };

  useEffect(() => {
    if (state.editData) {
      setInitialValues({
        typeName: state.editData.typeName,
        discountOffer: state.editData.discountOffer,
        discountType: state.editData.discountType,
        bannerType: state.editData.bannerType,
        status: state.editData.status,
      });
      setParkingImage(state.editData.imageUrl);
      setIsImgUrl(true);
    }
  }, []);

  useEffect(() => {
    if (uploadImageResponse.isSuccess && !state.editMode) {
      saveParkingType({
        token: token,
        payload: { ...submitData, imagePath: uploadImageResponse.data.imagePath },
      });
    } else if (uploadImageResponse.isSuccess && !isImgUrl && state.editMode) {
      updateParkingType({
        token: token,
        payload: { ...submitData, imagePath: uploadImageResponse.data.imagePath },
      });
    } else if (uploadImageResponse.isError) {
      toast.error('Image size is too large');
    }
    if (uploadImageResponse.isError) {
      let errorData: any = uploadImageResponse.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error('Something went wrong ' + msg || '');
    }
  }, [uploadImageResponse.isSuccess, uploadImageResponse.isError]);

  useEffect(() => {
    if (saveParkingDataResponse.isSuccess) {
      toast.success('Parking type added successfully');
      navigate('/dashboard/service-type', { replace: true });
    } else if (saveParkingDataResponse.isError) {
      let errorData: any = saveParkingDataResponse.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error('Something went wrong ' + msg || '');
    }

    if (updateParkingDataResponse.isSuccess) {
      toast.success('Parking type updated successfully');
      navigate('/dashboard/service-type', { replace: true });
    } else if (updateParkingDataResponse.isError) {
      let errorData: any = updateParkingDataResponse.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error('Something went wrong ' + msg || '');
    }
  }, [
    saveParkingDataResponse.isSuccess,
    saveParkingDataResponse.isError,
    updateParkingDataResponse.isSuccess,
    updateParkingDataResponse.isError,
  ]);

  const [getEnumData, enumResponse] = useGetEnumDataMutation();

  useEffect(() => {
    getEnumData({ token: token });
  }, []);

  useEffect(() => {
    if (enumResponse.isSuccess) {
      dispatch(setEnumData(enumResponse.data));
    }
  }, [enumResponse.isSuccess]);

  let enumData: any = useAppSelector(getSpecificEnumData);

  useEffect(() => {
    if (enumData) {
      setDiscountType(enumData['DISCOUNT_TYPE']);
    }
  }, [enumData]);

  return (
    <Box>
      <Container>
        <h1>{state.editMode ? 'Edit Service Type' : 'Add Service Type'}</h1>
        <ResponsiveBox style={{ display: 'flex', paddingTop: '40px' }}>
          <Box>
            <input
              type="file"
              name="parking-image"
              id="parking-image"
              ref={parkingImageInput}
              accept="image/gif, image/jpeg, image/png"
              onChange={(event: any) => {
                if (state.editMode && isImgUrl) {
                  setIsImgUrl(false);
                }
                setParkingImage(event.target.files[0]);
              }}
              hidden
            />
            <Box
              onClick={() => {
                parkingImageInput.current.click();
              }}
            >
              {parkingImage && (
                <Box style={{ position: 'relative' }}>
                  <img
                    src={state.editMode && isImgUrl ? parkingImage : URL.createObjectURL(parkingImage)}
                    alt=""
                    height={200}
                    width={200}
                    style={{ borderRadius: '100px' }}
                  />

                  <Box
                    style={{
                      background: 'white ',
                      padding: '3px',
                      borderRadius: '100px',
                      position: 'absolute',
                      bottom: '4px',
                      right: '40px',
                      fontSize: '34px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '40px',
                      width: '40px',
                    }}
                  >
                    <AddPhotoAlternateIcon />
                  </Box>
                </Box>
              )}
              {!parkingImage && (
                <Box
                  style={{
                    background: 'lightgray',
                    height: 200,
                    width: 200,
                    borderRadius: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <h5>Upload photo</h5>
                </Box>
              )}
            </Box>
            <Box style={{ textAlign: 'center', marginTop: '10px' }}>
              <p>Allowed *.jpeg, *.jpg, *.png, *.gif</p>
              <p> Max size of 3.1 MB</p>
            </Box>
          </Box>
          <FormikBox style={{ flex: 1, padding: 0 }}>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              onSubmit={handleAddOrUpdateParking}
              validationSchema={ParkingSchema}
            >
              {({ handleBlur, handleChange, handleSubmit, setFieldValue, values, touched, errors }) => (
                <Box>
                  <Box display={'flex'} alignItems={'center'} marginTop={'25px'} marginLeft={'25px'}>
                    <Box width={'100%'}>
                      <Stack direction="column" alignItems="self-start">
                        <Typography variant="h5" gutterBottom component="div">
                          Service Type Name
                        </Typography>
                        <TextField
                          fullWidth
                          autoComplete="typeName"
                          style={{
                            width: '80%',
                          }}
                          type="text"
                          name="typeName"
                          label="Enter Service type name"
                          value={values.typeName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched.typeName && errors.typeName)}
                          helperText={touched.typeName && errors.typeName}
                        />
                      </Stack>
                    </Box>
                    <Box style={{ width: '100%' }}>
                      <Typography variant="h5" gutterBottom component="div">
                        Discount Type
                      </Typography>
                      <Select
                        id="discountType"
                        name="discountType"
                        style={{ width: '100%' }}
                        value={values.discountType}
                        onChange={(event: any) => {
                          setFieldValue('discountType', event.target.value);
                        }}
                        onBlur={handleBlur}
                        error={Boolean(touched.discountType && errors.discountType)}
                        displayEmpty
                        defaultValue={''}
                      >
                        <MenuItem disabled value="">
                          Select discount type
                        </MenuItem>
                        {discountType &&
                          discountType.map((discountTypes: any) => (
                            <MenuItem key={discountTypes.code} value={discountTypes.code}>
                              {discountTypes.displayValue}
                            </MenuItem>
                          ))}
                      </Select>
                      {errors.discountType && (
                        <div style={{ color: 'red' }} className="text-sm">
                          {errors.discountType}
                        </div>
                      )}
                    </Box>
                  </Box>
                  <Box display={'flex'} alignItems={'center'} marginTop={'25px'} marginLeft={'25px'}>
                    <Box
                      style={{
                        width: '100%',
                        marginRight: '20px',
                      }}
                      display={values.discountType == 'DTND' ? 'none' : 'block'}
                    >
                      <Typography variant="h5" marginTop={2} gutterBottom component="div">
                        Discount {values.discountType === 'DTPRCNT' ? '%' : 'Amount'}
                      </Typography>
                      <TextField
                        style={{ width: '103%' }}
                        autoComplete=" discountOffer"
                        type="text"
                        name="discountOffer"
                        label="Enter discount"
                        value={values.discountOffer}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.discountOffer && errors.discountOffer)}
                        helperText={touched.discountOffer && errors.discountOffer}
                      />
                    </Box>
                  </Box>

                  <Box style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <Button
                      style={{ color: 'white', backgroundColor: '#E23744' }}
                      variant="contained"
                      color="error"
                      onClick={() => {
                        navigate('/dashboard/service-type');
                      }}
                      sx={{ marginRight: '10px' }}
                      size="large"
                    >
                      Cancel
                    </Button>
                    <Button
                      style={{ color: 'white', backgroundColor: '#E23744' }}
                      variant="contained"
                      size="large"
                      component="span"
                      onClick={() => {
                        if (!parkingImage) {
                          toast.error('Please upload an image');
                        } else {
                          handleSubmit();
                        }
                      }}
                    >
                      Save
                    </Button>
                  </Box>
                </Box>
              )}
            </Formik>
          </FormikBox>
        </ResponsiveBox>
      </Container>
      {saveParkingDataResponse.isLoading || updateParkingDataResponse.isLoading || uploadImageResponse.isLoading ? (
        <Loading />
      ) : null}
    </Box>
  );
};

export default AddOrUpdateParking;
